import React from "react";
import { CheckCircle, ShieldCheck, Clock, HeartHandshake } from "lucide-react";
import Seo from "../components/Seo";
import EnteteLanding from "../components/EnteteLanding";
import PiedLanding from "../components/PiedLanding";
import SimulateurRapide from "../components/SimulateurRapide";
import BoutonRappel from "../components/BoutonRappel";
import { serviceJsonLd } from "../utils/donneesStructurees";
import { formaterEuros, AIDE_MAX } from "../utils/plafonds";

/**
 * Page d'atterrissage Google Ads — MaPrimeAdapt' pour les personnes âgées.
 *
 * Écrite pour l'enfant aidant plus que pour le parent : c'est lui qui cherche
 * sur internet, qui compare, et qui remplira le formulaire. D'où le vouvoiement
 * qui parle du « proche » et non de « vous », et la place donnée aux questions
 * qu'il se pose en premier — qui décide, qui paie, combien de temps.
 *
 * En noindex et hors sitemap : /maprimeadapt et /aidants couvrent déjà ce
 * sujet en organique.
 */

const ETAPES = [
  {
    titre: "1. Vérifier l'éligibilité",
    delai: "Un appel de 10 minutes",
    texte:
      "Trois conditions : avoir 70 ans ou plus (ou une perte d'autonomie reconnue GIR 1 à 6 dès 60 ans), être propriétaire occupant ou locataire du parc privé avec accord du bailleur, et des ressources sous les plafonds ANAH.",
  },
  {
    titre: "2. La visite à domicile",
    delai: "Sous 72 heures",
    texte:
      "Un évaluateur passe chez votre proche, regarde les pièces réellement problématiques et établit le diagnostic autonomie exigé par l'ANAH. Vous pouvez être présent, c'est même souhaitable.",
  },
  {
    titre: "3. Le dossier ANAH",
    delai: "Environ 8 jours",
    texte:
      "Nous rassemblons les pièces, faisons chiffrer les travaux par des artisans, et déposons le dossier. Vous n'avez aucune démarche administrative à faire.",
  },
  {
    titre: "4. L'accord, puis les travaux",
    delai: "2 à 3 mois d'instruction",
    texte:
      "L'ANAH notifie sa décision par écrit. Les travaux ne commencent qu'après : un chantier démarré avant fait perdre l'aide, définitivement.",
  },
  {
    titre: "5. Le versement",
    delai: "Après réception",
    texte:
      "L'aide est versée sur présentation des factures, une fois les travaux terminés et réceptionnés.",
  },
];

const QUESTIONS = [
  {
    q: "Mon parent doit-il avancer l'argent ?",
    r: "Oui, l'aide est versée après les travaux, sur factures. C'est le point à anticiper. Certaines caisses de retraite versent une avance, et la TVA à 5,5 % réduit la facture dès le départ. Nous en parlons dès le premier échange pour qu'il n'y ait pas de mauvaise surprise.",
  },
  {
    q: "Peut-on faire le dossier à sa place ?",
    r: "Vous pouvez tout piloter, mais c'est votre parent qui signe : c'est lui le propriétaire et le bénéficiaire. En pratique, beaucoup de familles font l'appel et la visite ensemble, et nous adressons les documents aux deux.",
  },
  {
    q: "Et s'il est locataire ?",
    r: "C'est possible dans le parc privé, avec l'accord écrit du propriétaire. En logement social, le dispositif ne s'applique pas de la même façon : appelez-nous, nous vous dirons en deux minutes si le dossier tient.",
  },
  {
    q: "Combien coûte votre accompagnement ?",
    r: "Rien pour vous. La prestation d'AMO est prise en charge par MaPrimeAdapt' — c'est prévu par le dispositif. Nous ne prenons aucune commission sur les devis des artisans.",
  },
  {
    q: "Combien de temps au total ?",
    r: "Comptez quatre à six mois entre le premier appel et la fin des travaux, dont deux à trois mois d'instruction ANAH sur lesquels personne n'a la main. Si la situation est urgente, dites-le : certains dossiers peuvent être signalés.",
  },
];

const LandingSeniors = () => (
  <div className="min-h-screen bg-white">
    <Seo
      titre="MaPrimeAdapt' pour seniors Île-de-France | AMO agréé ANAH"
      description="Votre parent vieillit dans un logement inadapté ? MaPrimeAdapt' finance jusqu'à 70 % des travaux. Nous montons le dossier ANAH de A à Z, gratuitement. Île-de-France, réponse sous 24 h."
      chemin="/maprimeadapt-personnes-agees-idf"
      image="/images/main-courante.webp"
      noindex
      donnees={[
        serviceJsonLd({
          nom: "Accompagnement MaPrimeAdapt' pour personnes âgées",
          description:
            "Assistance à maîtrise d'ouvrage pour l'adaptation du logement des personnes âgées en Île-de-France : diagnostic autonomie, montage et suivi du dossier ANAH.",
          url: "/maprimeadapt-personnes-agees-idf",
        }),
      ]}
    />
    <EnteteLanding />

    <main id="contenu">
      <section className="bg-gradient-to-br from-green-50 to-white py-10 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-full text-sm font-medium">
                <ShieldCheck className="h-4 w-4 mr-2" aria-hidden="true" />
                AMO agréé ANAH — Île-de-France
              </div>

              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                {/* Espace explicite : le span est en block, l'espace ne se voit
                    pas, mais sans lui le H1 lu se colle au tiret. */}
                Adaptez votre logement au vieillissement —{" "}
                <span className="text-green-700 block mt-2">
                  accompagnement gratuit par un AMO
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-gray-900 leading-relaxed">
                Une baignoire qu&apos;on n&apos;enjambe plus, un escalier qui fait peur,
                une chute évitée de justesse. MaPrimeAdapt&apos; finance jusqu&apos;à{" "}
                <strong>70 % des travaux</strong>, dans la limite de{" "}
                <strong>{formaterEuros(AIDE_MAX.tresModestes)}</strong>. Nous montons
                le dossier ANAH de bout en bout, <strong>sans que cela vous coûte quoi
                que ce soit</strong>.
              </p>

              <div className="bg-white border-2 border-green-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <HeartHandshake className="h-6 w-6 text-green-700 flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <p className="font-bold text-gray-900 mb-1">
                      Vous vous renseignez pour un parent ?
                    </p>
                    <p className="text-gray-700">
                      C&apos;est le cas de la plupart des familles qui nous appellent.
                      Vous pouvez tout organiser : l&apos;appel, la visite, le suivi.
                      Seule la signature revient à votre parent.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <BoutonRappel
                  libelle="Être rappelé gratuitement"
                  variant="default"
                  className="bg-green-700 hover:bg-green-800 text-white text-lg min-h-[56px] w-full sm:w-auto"
                />
                <a
                  href="tel:0939035444"
                  className="inline-flex items-center justify-center gap-2 border-2 border-green-700 text-green-800 hover:bg-green-50 rounded-lg px-6 text-lg font-semibold min-h-[56px] w-full sm:w-auto"
                >
                  09 39 03 54 44
                </a>
              </div>
            </div>

            <SimulateurRapide />
          </div>
        </div>
      </section>

      {/* Les démarches, étape par étape */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Les démarches, de bout en bout
              </h2>
              <p className="text-xl text-gray-700">
                Ce que nous faisons, ce que vous n&apos;avez pas à faire, et combien de
                temps chaque étape prend réellement.
              </p>
            </div>

            <ol className="space-y-5">
              {ETAPES.map((etape) => (
                <li
                  key={etape.titre}
                  className="bg-gray-50 border-l-4 border-green-700 rounded-r-xl p-6"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{etape.titre}</h3>
                    <span className="text-green-800 font-semibold text-sm bg-green-100 px-3 py-1 rounded-full">
                      {etape.delai}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{etape.texte}</p>
                </li>
              ))}
            </ol>

            <div className="text-center mt-12">
              <BoutonRappel
                libelle="Faire le point sur la situation"
                variant="default"
                className="bg-green-700 hover:bg-green-800 text-white text-lg min-h-[56px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Travaux les plus fréquents */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Ce que financent nos dossiers, concrètement
              </h2>
              <p className="text-xl text-gray-700">
                Les travaux les plus demandés dans les logements que nous adaptons.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                ["/images/douche-securisee.webp", "Douche de plain-pied", "Remplacer la baignoire est le premier poste de tous nos dossiers."],
                ["/images/monte-escalier.webp", "Monte-escalier", "Pour rester à l'étage plutôt que de descendre un lit au salon."],
                ["/images/main-courante.webp", "Barres d'appui et mains courantes", "Dans la salle de bain, les WC, le couloir et l'escalier."],
                ["/images/wc-sureleve.webp", "WC surélevé", "Assise relevée et barre de relevage, pour se lever sans aide."],
                ["/images/elargissement-portes.webp", "Élargissement des portes", "Indispensable dès qu'un déambulateur ou un fauteuil entre en jeu."],
                ["/images/volets-automatiques.webp", "Volets roulants motorisés", "Éviter les gestes en hauteur et les manivelles bloquées."],
              ].map(([image, nom, texte]) => (
                <div key={nom} className="bg-white rounded-xl border-2 border-gray-100 overflow-hidden">
                  <img
                    src={image}
                    alt={nom}
                    width="600"
                    height="400"
                    loading="lazy"
                    className="w-full h-44 object-cover"
                  />
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2">{nom}</h3>
                    <p className="text-gray-700">{texte}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Questions des familles */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">
              Les questions que les familles posent en premier
            </h2>
            <p className="text-xl text-gray-700 mb-10 text-center">
              Y compris celles dont la réponse ne nous arrange pas.
            </p>

            <div className="space-y-5">
              {QUESTIONS.map((item) => (
                <div key={item.q} className="border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.r}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Appel à l'action final */}
      <section className="py-16 lg:py-24 bg-green-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Un appel suffit à savoir où vous en êtes
            </h2>
            <p className="text-xl text-green-50 mb-10 max-w-2xl mx-auto">
              Dites-nous l&apos;âge de votre proche, s&apos;il est propriétaire, et
              approximativement ses revenus. Nous vous dirons tout de suite si le
              dossier tient — et s&apos;il ne tient pas, quelles autres aides existent.
            </p>

            <ul className="grid sm:grid-cols-3 gap-4 text-left mb-10">
              {[
                "Réponse sous 24 h ouvrées",
                "Diagnostic à domicile gratuit",
                "Aucun engagement",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3 bg-green-800 rounded-lg p-4">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <BoutonRappel
                libelle="Choisir un créneau de rappel"
                variant="default"
                className="bg-white text-green-800 hover:bg-green-50 text-lg min-h-[56px] w-full sm:w-auto"
              />
              <a
                href="tel:0939035444"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-green-800 rounded-lg px-6 text-lg font-semibold min-h-[56px] w-full sm:w-auto"
              >
                09 39 03 54 44
              </a>
            </div>

            <p className="text-green-100 mt-6 flex items-center justify-center gap-2">
              <Clock className="h-5 w-5" aria-hidden="true" />
              Du lundi au jeudi 10 h - 17 h, vendredi 10 h - 13 h
            </p>
          </div>
        </div>
      </section>
    </main>

    <PiedLanding />
  </div>
);

export default LandingSeniors;
